import { AmsUserLookup } from '../../../../Interfaces/Common/AmsUserLookup';
import { AmsLateFee } from '../../../../Interfaces/Common/AmsLateFee';
import {FinanceLoanClass, getFullName, hasValue, LookupItem, Organization} from '@amsanalytics/react-shared-library';
import {
  ICheckAndReserveAmsCustomerNumber, 
  ILosCustomerIdAndAmsCustomerNumberPair,
  IPostAmsCustomer,
  IPreviousAmsCustomerDetailWithNames
} from "../../../../Interfaces/Common/IPostAmsLoan";
import {IAppCustomer} from "../../../../Interfaces/Application/IAppCustomer";
import {IApplication} from "../../../../Interfaces/Application/IApplication";
import {getApplicant} from "../../../../utils/appUtils";
import {Constants} from "../../../../utils/constants";
import {AmsCustomerNumberCreationMethod} from "../../../../Data/ConfigurationData";
import {AmsCustomerPreCheckSearchResult} from "../../../../Interfaces/Common/AmsCustomerPreCheckSearchResult";
import {authService} from "../../../../index";
import {httpPostAsync} from "../../../../utils/HttpClient";
import {ApplicationDispatch, SET_SERVER_MESSAGE} from "../../../../store/application/types";

export interface AmsLookupForComboBoxDisplay {
  id: string;
  code: string;
  description: string;
}

export const getAmsLookupForComboBoxDisplay = (id: string | null | undefined, amsCode: string | null | undefined, amsDescription: string | null | undefined): AmsLookupForComboBoxDisplay => {
  return {
    id: id ?? "",
    code: amsCode ?? "",
    description: `${amsCode ?? ""} - ${amsDescription ?? ""}`
  };
};

export const getFinanceLoanClassesToDisplay = (financeLoanClasses: FinanceLoanClass[]): AmsLookupForComboBoxDisplay[] => {
  return financeLoanClasses.map(financeLoanClass => {
    return getAmsLookupForComboBoxDisplay(financeLoanClass.id, financeLoanClass.code, financeLoanClass.description);
  });
};

export const getAmsLocationsOfSaleToDisplay = (organizations: Organization[]): AmsLookupForComboBoxDisplay[] => {
  return organizations.map(organization => {
    return getAmsLookupForComboBoxDisplay(organization.id, organization.amsCode, organization.description);
  })
};

export const getAmsLookupToDisplay = (amsLookup: LookupItem): AmsLookupForComboBoxDisplay => {
  return getAmsLookupForComboBoxDisplay(amsLookup.id, amsLookup.name, amsLookup.description);
};

export const getAmsUserToDisplay = (amsUser: AmsUserLookup): AmsLookupForComboBoxDisplay => {
  return getAmsLookupForComboBoxDisplay(amsUser.id, amsUser.employeeNumber, getFullName(amsUser));
};

export const getAmsUsersToDisplay = (amsUsers: AmsUserLookup[]): AmsLookupForComboBoxDisplay[] => {
  return amsUsers.map(amsUser => {
    return getAmsUserToDisplay(amsUser);
  });
};

// Standardizes how a lookup value object is displayed in a combobox
export const getAmsLookupsToDisplay = (amsLookups: LookupItem[]): AmsLookupForComboBoxDisplay[] => {
  return amsLookups.map(amsLookup => {
    return getAmsLookupToDisplay(amsLookup);
  })};

export const getAmsLateFeeToDisplay = (lateFee: AmsLateFee): AmsLookupForComboBoxDisplay => {
  return {
    id: lateFee.id,
    code: lateFee.code ?? "",
    description: `${lateFee.code ?? ""} - ${lateFee.lateDays} DAYS, $${lateFee.lateFee}, OR ${lateFee.latePercent} (${lateFee.lateGreaterThanOrLessThan}) ON UNPAID`
  };
};

export const getAmsLateFeesToDisplay = (lateFees: AmsLateFee[]): AmsLookupForComboBoxDisplay[] => {
  return lateFees.map(lf => getAmsLateFeeToDisplay(lf));
};

const trimAndTruncateString = (inputString: string | undefined, length: number) => {
  return inputString?.trim().slice(0, length);
}

const getDefaultCustomerNumberByRole = (baseValue: string, customerRoleId: string) => {
  switch (customerRoleId.toLowerCase()) {
    case Constants.roleApplicantId.toLowerCase():
      return baseValue;
    case Constants.roleCoApplicant1Id.toLowerCase():
      return baseValue.concat("C");
    case Constants.roleCoApplicant2Id.toLowerCase():
      return baseValue.concat("CC");
    default:
      return undefined;
  }
}

const getDefaultCustomerNumber = (
  appCustomer: IAppCustomer, 
  stockNumber: string | undefined, 
  applicationNo: number,
  customerNumberCreationMethod?: AmsCustomerNumberCreationMethod | undefined
): string | undefined => {
  const customerRoleId = appCustomer.customerRoleId;
  const applicationNumberBasedCustomerNumber = getDefaultCustomerNumberByRole(applicationNo.toString(), customerRoleId);

  switch (customerNumberCreationMethod) {
    // Uses the LOS customer number because it is unique to the LOS and AMS's convention is padding an int with leading 0s. 
    // This means that our customer numbers (12345, 12346, etc.) won't ever match up with AMS's 10 characters (0000012345, 0000012346, etc.)
    case AmsCustomerNumberCreationMethod.SerialNumber:
      return appCustomer.customer.customerNumber.toString();

    // They'll type in manually
    case AmsCustomerNumberCreationMethod.Freehand:
      return undefined;

    case AmsCustomerNumberCreationMethod.ApplicationNumber:
      return applicationNumberBasedCustomerNumber;

    // Default
    case AmsCustomerNumberCreationMethod.CarStockNumber:
    default:
      if (hasValue(stockNumber)) {
        return getDefaultCustomerNumberByRole(stockNumber!, customerRoleId);
      }
      else {
        // If the stock number isn't populated, just return the one for the application number
        return applicationNumberBasedCustomerNumber;
      }
  }
}

export const defaultManualEntryCustomerNumberOption: IPreviousAmsCustomerDetailWithNames = {
  firstName: '',
  middleName: '',
  lastName: '',
  amsCustomerNumber: '',
  ssn: '',
  summary: 'MANUAL ENTRY',
  optionForManualEntry: true
};

export const maxAmsAccountNumberLength = 8;
export const maxAmsCustomerNumberLength = 10;

export const getDefaultCustomerDetailsForPosting = (
  currentApp: IApplication,
  customerNumberCreationMethod: AmsCustomerNumberCreationMethod | undefined
): IPostAmsCustomer[] => {
  const primary = currentApp && getApplicant(currentApp, Constants.roleApplicantId, true);
  const coApplicant1 = currentApp && getApplicant(currentApp, Constants.roleCoApplicant1Id, true);
  const coApplicant2 = currentApp && getApplicant(currentApp, Constants.roleCoApplicant2Id, true);
  const sortedCustomers = [primary, coApplicant1, coApplicant2].filter(appCustomer => appCustomer !== undefined);
  
  return sortedCustomers.map((ac) => {
    const customer = ac!.customer!;
    return {
      firstName: customer.firstName,
      middleName: customer.middleName,
      lastName: customer.lastName,
      customerId: customer.id,
      ssn: customer.ssn,
      amsCustomerNumber: hasValue(customer.amsCustomerNumber)
        ? customer.amsCustomerNumber ?? ''
        : trimAndTruncateString(getDefaultCustomerNumber(ac!, currentApp.vehicle?.stockNumber, currentApp.applicationNo, customerNumberCreationMethod), maxAmsCustomerNumberLength) ?? '',
      selectedPreviousCustomerFromAms: defaultManualEntryCustomerNumberOption,
      lastNameMismatch: false,
      confirmSelectedNameIsDifferentThanApplicant: false,
      updateInsteadOfCreate: false,
      autoAccrueOneCpiPaymentWhenPosting: false,
      loanCpiDetails: null,
      deferredDownCpiDetails: null
    };
  });
}

export const getAtLeastOneCustomerNumberIsTooLongError = (customerDetails: IPostAmsCustomer[] | undefined, maxAmsCustomerNumberLength: number): string | undefined => {
  return customerDetails?.some(c => c.amsCustomerNumber.length > maxAmsCustomerNumberLength)
    ? `At least one customer number longer than the max- ${maxAmsCustomerNumberLength}`
    : undefined;
}

export const getAmsCustomersForAmsCustomerNumbersAsync = async (
  losCustomerIdAndAmsCustomerNumberPairs: ILosCustomerIdAndAmsCustomerNumberPair[], 
  reserveAfterChecking: boolean, 
  dispatch: ApplicationDispatch
): Promise<AmsCustomerPreCheckSearchResult[]> => {
  const requestBody: ICheckAndReserveAmsCustomerNumber = {
    reserveAmsCustomerNumbersAfterCheck: reserveAfterChecking,
    losCustomerIdAndAmsCustomerNumberPairs: losCustomerIdAndAmsCustomerNumberPairs
  };
  
  // If none were entered manually and thus, none need to be checked, return early
  if (requestBody.losCustomerIdAndAmsCustomerNumberPairs.length === 0) {
    return [];
  }

  try {
    const bearerToken = await authService.getToken();
    const url = 'api/Ams/GetPreviousAmsCustomers';
    const response: Response | undefined = await httpPostAsync(
      url,
      requestBody,
      bearerToken
    );

    if (!response) {
      dispatch({
        type: SET_SERVER_MESSAGE,
        payload: { isError: true, message: 'No response was received from the server. You can try refreshing the browser. If the problem persists, please contact support.'},
      });
      return [];
    }

    if (response.ok) {
      const amsCustomerPreCheckSearchResult: AmsCustomerPreCheckSearchResult[] = await response.json();
      return amsCustomerPreCheckSearchResult;
    } else {
      const result = await response.json();
      dispatch({
        type: SET_SERVER_MESSAGE,
        payload: { isError: true, message: result as string },
      });
    }
  } catch (err) {
    dispatch({
      type: SET_SERVER_MESSAGE,
      payload: { isError: true, message: err as string},
    });
  }
  return [];
};

export const getLosCustomerIdAndAmsCustomerNumberPairs = (customerDetails: IPostAmsCustomer[]): ILosCustomerIdAndAmsCustomerNumberPair[] => {
  return customerDetails
    .filter(cd => cd.selectedPreviousCustomerFromAms.optionForManualEntry && hasValue(cd.amsCustomerNumber))
    .map(cd => {
      return {
        customerId: cd.customerId,
        amsCustomerNumber: cd.amsCustomerNumber
      }
    });
}